import type { OrganizationRole } from "@/api/types";

/**
 * What each role may do, transcribed from the endpoint descriptions in openapi.yaml.
 *
 * HIDING A BUTTON IS NOT SECURITY. The server enforces every one of these, and it is the
 * only thing that does. This table exists because rendering a control that is guaranteed
 * to come back 403 is a bug: it teaches the operator that the product is broken.
 *
 * Kept as one table so a reviewer can diff it against the spec in one pass.
 */
export type Permission =
  // organization
  | "org:update"
  // members & invitations
  | "members:read"
  | "members:manage" // change role, remove
  | "members:grantOwner" // owner-only, per spec
  | "invitations:manage"
  // inventory — sites, regions, generators, tanks
  | "sites:read"
  | "sites:write"
  | "sites:delete"
  // fuel ledger
  | "refills:read"
  | "refills:create"
  | "refills:update"
  | "refills:delete"
  // requests
  | "requests:read"
  | "requests:create"
  | "requests:decide" // approve / decline
  // reports
  | "reports:read"
  | "reports:export"
  // api keys
  | "apiKeys:read"
  | "apiKeys:manage" // owner-only, per spec
  // field boards
  | "devices:read"
  | "devices:logs" // owner/admin — board logs name Wi-Fi networks and addresses
  | "devices:manage" // owner/admin — rename, link a generator, send console commands
  | "firmware:read" // owner/admin
  | "firmware:upload"; // owner-only, per spec

const MATRIX: Record<OrganizationRole, ReadonlySet<Permission>> = {
  owner: new Set<Permission>([
    "org:update",
    "members:read",
    "members:manage",
    "members:grantOwner",
    "invitations:manage",
    "sites:read",
    "sites:write",
    "sites:delete",
    "refills:read",
    "refills:create",
    "refills:update",
    "refills:delete",
    "requests:read",
    "requests:create",
    "requests:decide",
    "reports:read",
    "reports:export",
    "apiKeys:read",
    "apiKeys:manage",
    "devices:read",
    "devices:logs",
    "devices:manage",
    "firmware:read",
    "firmware:upload",
  ]),
  admin: new Set<Permission>([
    "org:update",
    "members:read",
    "members:manage",
    "invitations:manage",
    "sites:read",
    "sites:write",
    "sites:delete",
    "refills:read",
    "refills:create",
    "refills:update",
    "refills:delete",
    "requests:read",
    "requests:create",
    "requests:decide",
    "reports:read",
    "reports:export",
    // `apiKeys:read` — the spec lets an admin LIST keys, but only an owner may mint or revoke.
    "apiKeys:read",
    // Same shape as keys: an admin runs the boards and sees the firmware, only an owner
    // uploads a new image.
    "devices:read",
    "devices:logs",
    "devices:manage",
    "firmware:read",
  ]),
  operator: new Set<Permission>([
    "members:read",
    "sites:read",
    "refills:read",
    "refills:create",
    // The spec allows an operator to update a refill *they recorded*. Ownership of the row
    // is not something a role can express, so `canEditRefill()` below decides that case.
    "requests:read",
    "requests:create",
    "reports:read",
    "reports:export",
    "devices:read",
  ]),
  viewer: new Set<Permission>([
    "members:read",
    "sites:read",
    "refills:read",
    "requests:read",
    "reports:read",
    "reports:export",
    "devices:read",
  ]),
};

export function can(role: OrganizationRole | undefined, permission: Permission): boolean {
  if (!role) return false;
  return MATRIX[role].has(permission);
}

/**
 * "Requires `owner`, `admin`, or the `operator` who recorded it." — openapi.yaml, updateRefill.
 * A role alone cannot answer this; it needs the row and the caller.
 */
export function canEditRefill(
  role: OrganizationRole | undefined,
  recordedByUserId: string,
  currentUserId: string,
): boolean {
  if (can(role, "refills:update")) return true;
  return role === "operator" && recordedByUserId === currentUserId;
}

/** A suspended org, or a support impersonation session, is read-only no matter the role. */
export function isReadOnly(opts: {
  orgStatus?: "active" | "suspended";
  impersonated?: boolean;
}): boolean {
  return opts.orgStatus === "suspended" || opts.impersonated === true;
}
